import React, { useState } from 'react';
import { Button, Card, Subtitle2, Text, makeStyles, tokens } from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronUp20Regular, CheckmarkCircle16Filled, Warning16Filled } from '@fluentui/react-icons';
import TextMessage from './TextMessage';
import Script from '../Script/Script';
import Collapse from '../Collapse';

const useStyles = makeStyles({
    messageContainer: {
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: '#292C31',
        borderRadius: '16px',
        color: '#fff',
        padding: '16px',
        marginBottom: '16px',
    },
    executionTime: {
        fontSize: '12px',
        color: '#999',
        marginTop: '8px',
    },
    toggleButton: {
        width: 'fit-content',
        borderRadius: '8px',
    },
    segmentScriptContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    approachContainer: {
        marginBottom: '12px',
    },
    rawDataPre: {
        fontSize: '11px',
        color: '#999',
        padding: '8px',
        maxHeight: '300px',
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
    },
    validationBanner: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS,
        marginTop: tokens.spacingVerticalS,
        marginBottom: tokens.spacingVerticalS,
        paddingTop: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalS,
        paddingLeft: tokens.spacingHorizontalM,
        paddingRight: tokens.spacingHorizontalM,
        borderRadius: tokens.borderRadiusMedium,
    },
    validationValid: {
        backgroundColor: tokens.colorStatusSuccessBackground1,
        border: `1px solid ${tokens.colorStatusSuccessBorder1}`,
        color: tokens.colorStatusSuccessForeground1,
    },
    validationSuspicious: {
        backgroundColor: tokens.colorStatusWarningBackground1,
        border: `1px solid ${tokens.colorStatusWarningBorder1}`,
        color: tokens.colorStatusWarningForeground1,
    },
    validationHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
        fontWeight: tokens.fontWeightSemibold,
    },
    validationReasoning: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground1,
    },
});

interface AnalyticsMessageProps {
    msg: any;
    index: number;
    onRetry?: (message: any, validationReasoning: string) => void;
    retryDisabled?: boolean;
}

const AnalyticsMessage: React.FC<AnalyticsMessageProps> = ({ msg, index, onRetry, retryDisabled = false }) => {
    const classes = useStyles();
    const [viewScript, setViewScript] = useState(false);
    const [viewRawData, setViewRawData] = useState(false);

    if (msg.role === 'User') {
        return (
            <TextMessage
                key={index}
                message={msg.userInput ?? ''}
                isBot={false}
                hardcoded={false}
            />
        );
    }

    const executeTimeMs = msg.executeTimeMs;
    const script = msg.script;
    const rawData = msg.unparsedGptResponse;
    const scriptDescription = msg.scriptDescription;
    const validation = msg.validation;

    return (
        <Card className={classes.messageContainer}>
            {scriptDescription && scriptDescription !== msg.responseText && (
                <div className={classes.approachContainer}>
                    <TextMessage
                        key={`${index}-approach`}
                        message={`**Approach:** ${scriptDescription}`}
                        isBot={true}
                        hardcoded={false}
                    />
                </div>
            )}
            <TextMessage
                key={index}
                message={msg.responseText ?? ''}
                isBot={true}
                hardcoded={false}
            />
            {executeTimeMs != null && executeTimeMs > 0 && (
                <Text className={classes.executionTime}>
                    Execution Time: {Math.floor(executeTimeMs / 1000)}s
                </Text>
            )}
            {validation && (
                <div
                    className={`${classes.validationBanner} ${
                        validation.resultsLookValid
                            ? classes.validationValid
                            : classes.validationSuspicious
                    }`}
                >
                    <div className={classes.validationHeader}>
                        {validation.resultsLookValid ? (
                            <CheckmarkCircle16Filled />
                        ) : (
                            <Warning16Filled />
                        )}
                        <Text>
                            {validation.resultsLookValid
                                ? 'Results look valid'
                                : 'Results look suspicious'}
                        </Text>
                    </div>
                    {validation.reasoning && (
                        <Text className={classes.validationReasoning}>
                            {validation.reasoning}
                        </Text>
                    )}
                    {!validation.resultsLookValid && onRetry && (
                        <Button
                            appearance="subtle"
                            size="small"
                            disabled={retryDisabled}
                            style={{ alignSelf: 'flex-start', marginTop: '4px' }}
                            onClick={() => onRetry(msg, validation.reasoning ?? '')}
                        >
                            {retryDisabled ? 'Retrying...' : 'Retry'}
                        </Button>
                    )}
                </div>
            )}
            {script && (
                <div>
                    <Button
                        appearance="subtle"
                        iconPosition="after"
                        className={classes.toggleButton}
                        onClick={() => setViewScript(!viewScript)}
                        icon={viewScript ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
                    >
                        <Subtitle2>View Script</Subtitle2>
                    </Button>
                    <Collapse in={viewScript}>
                        <div className={classes.segmentScriptContainer}>
                            <Script script={script} isAnalytics={true} />
                        </div>
                    </Collapse>
                </div>
            )}
            {rawData && rawData !== '' && rawData !== '{}' && (
                <div>
                    <Button
                        appearance="subtle"
                        iconPosition="after"
                        className={classes.toggleButton}
                        onClick={() => setViewRawData(!viewRawData)}
                        icon={viewRawData ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
                    >
                        <Subtitle2>Raw Data</Subtitle2>
                    </Button>
                    <Collapse in={viewRawData}>
                        <pre className={classes.rawDataPre}>
                            {(() => { try { return JSON.stringify(JSON.parse(rawData), null, 2); } catch { return rawData; } })()}
                        </pre>
                    </Collapse>
                </div>
            )}
        </Card>
    );
};

export default React.memo(AnalyticsMessage);
